import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from '../locales/en.json';
import { DEFAULT_LANGUAGE, dirForLanguage } from './languages';

const isDev = import.meta.env?.DEV ?? false;

// Locale bundles are ~170 KB each raw. Only English ships in the main bundle
// (it is the fallback language and must be available synchronously for tests
// and for missing-key fallback); he/fr/de are code-split into their own chunks
// and fetched on demand by the backend plugin below. Vite turns each dynamic
// import into a separate chunk.
const lazyLocales: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  he: () => import('../locales/he.json'),
  fr: () => import('../locales/fr.json'),
  de: () => import('../locales/de.json'),
};

// Minimal i18next backend: serves the bundled English synchronously and lazy
// chunks for everything else. i18next's changeLanguage() waits for the backend
// before emitting languageChanged, so runtime language switches never render
// untranslated keys.
const lazyLocaleBackend = {
  type: 'backend' as const,
  init() {},
  read(
    lng: string,
    _ns: string,
    callback: (err: unknown, data: Record<string, unknown> | null) => void,
  ) {
    if (lng === 'en') {
      callback(null, en as Record<string, unknown>);
      return;
    }
    const loader = lazyLocales[lng];
    if (!loader) {
      callback(null, {});
      return;
    }
    loader()
      .then((mod) => callback(null, mod.default))
      .catch((err) => callback(err, null));
  },
};

/**
 * Resolves once the initial language (and the English fallback) is loaded.
 * main.tsx awaits this before the first render so non-English users never see
 * a flash of untranslated keys.
 */
export const i18nReady: Promise<unknown> = i18n
  .use(lazyLocaleBackend)
  .use(initReactI18next)
  .init({
    lng: DEFAULT_LANGUAGE,
    fallbackLng: DEFAULT_LANGUAGE,
    interpolation: { escapeValue: false },
    // English resolves synchronously through the backend, so init completes in
    // the same tick for the default language (keeps tests synchronous too).
    initImmediate: false,
    // No <Suspense> requirement: the initial load is gated by i18nReady and
    // runtime switches are gated by changeLanguage's backend await.
    react: { useSuspense: false },
    // Show the English fallback instead of a blank string when a key is missing
    // in the active language. Keeps the UI legible while translations catch up.
    returnEmptyString: false,
    // In development, surface any key that is referenced in code but absent from
    // the locale files. This is how we catch strings that slipped extraction.
    saveMissing: isDev,
    missingKeyHandler: isDev
      ? (lngs, _ns, key) => {
          console.warn(`[i18n] missing key "${key}" for languages: ${lngs.join(', ')}`);
        }
      : undefined,
  });

function applyDirection(lng: string) {
  document.documentElement.dir = dirForLanguage(lng);
  document.documentElement.lang = lng;
}

// Apply direction (RTL/LTR) whenever the language changes. Nothing is persisted: the language is
// the CLUB's setting, served on GET /auth/profile and applied by lib/clubLanguage.ts. It used to
// live in localStorage['language'], which was per browser origin — not per user and not per club —
// so a super admin's choice followed them into every club they acted as, and it outlived sign-out
// into the next person on a shared machine.
i18n.on('languageChanged', (lng) => {
  applyDirection(lng);
});

// Apply direction once on initial load so the first paint is correct.
applyDirection(i18n.language ?? DEFAULT_LANGUAGE);

export default i18n;
